import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useParticleWorker } from '../hooks/useParticleWorker';

interface ParticleData {
  id: string;
  x: number;
  y: number;
  size: number;
  color: string;
  opacity: number;
  needsUpdate: boolean;
}

interface PerformanceMetrics {
  averageFrameTime: number;
  droppedFrames: number;
  particleCount: number;
  lastUpdate: number;
}

interface WorkerParticleCanvasProps {
  className?: string;
  style?: React.CSSProperties;
  particleCount?: number;
  enableGlow?: boolean;
  enableTrails?: boolean;
  performanceMode?: 'high' | 'medium' | 'low';
  onPerformanceChange?: (mode: string, metrics: PerformanceMetrics | null) => void;
  interactive?: boolean;
  colors?: string[];
  isVisible?: boolean;
}

export const WorkerParticleCanvas: React.FC<WorkerParticleCanvasProps> = ({
  className = '',
  style = {},
  particleCount = 20,
  enableGlow = true,
  enableTrails = true,
  performanceMode = 'high',
  onPerformanceChange,
  interactive = false,
  colors = ['#00ff41', '#0066ff', '#00d4ff'],
  isVisible = true
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [lastRenderTime, setLastRenderTime] = useState(0);
  const particlesRef = useRef<ParticleData[]>([]);

  // Initialize particle worker with configuration
  const {
    isWorkerReady,
    isRunning,
    currentPerformanceMode,
    performanceMetrics,
    initializeParticleSystem,
    startAnimation,
    stopAnimation,
    addParticleAt,
    setPerformanceMode,
    isSupported
  } = useParticleWorker({
    autoStart: false,
    performanceMode,
    enablePerformanceMonitoring: true,
    onPerformanceModeChange: (mode, reason) => {
      console.log(`[Worker Particle Canvas] Performance mode changed: ${mode} - ${reason}`);
      if (onPerformanceChange) {
        onPerformanceChange(mode, performanceMetrics);
      }
    },
    onFrameUpdate: useCallback((particles: ParticleData[], metrics?: PerformanceMetrics) => {
      // Store particles for rendering
      particlesRef.current = particles;
      
      // Trigger render on next animation frame
      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(renderParticles);
      }
    }, [])
  });

  // Check worker support
  if (!isSupported()) {
    console.warn('[Worker Particle Canvas] Web Workers not supported, falling back to main thread');
  }

  // Initialize canvas and context
  const initializeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { 
      alpha: true, 
      desynchronized: true,
      willReadFrequently: false
    });
    
    if (!ctx) return;

    ctxRef.current = ctx;

    // Set canvas size
    const resizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, performanceMode === 'high' ? 2 : 1);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(dpr, dpr);
      
      // Set canvas optimization properties
      ctx.imageSmoothingEnabled = performanceMode === 'high';
      ctx.globalCompositeOperation = enableTrails ? 'source-over' : 'source-over';
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [performanceMode, enableTrails]);

  // Particle rendering function
  const renderParticles = useCallback((currentTime: number = performance.now()) => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx || !isVisible) {
      animationFrameRef.current = null;
      return;
    }

    // Performance throttling - limit to 60fps max
    if (currentTime - lastRenderTime < 16.67) {
      animationFrameRef.current = requestAnimationFrame(renderParticles);
      return;
    }

    const particles = particlesRef.current;
    if (!particles || particles.length === 0) {
      animationFrameRef.current = null;
      return;
    }

    // Clear canvas with trail effect
    if (enableTrails) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
    } else {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    }

    // Render particles
    particles.forEach((particle) => {
      if (particle.opacity <= 0) return;

      ctx.save();
      ctx.globalAlpha = particle.opacity;
      ctx.fillStyle = particle.color;

      // Apply glow effect based on performance mode and settings
      if (enableGlow && performanceMode !== 'low') {
        const glowIntensity = performanceMode === 'high' ? 8 : 4;
        ctx.shadowBlur = glowIntensity;
        ctx.shadowColor = particle.color;
      }

      // Render particle
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    });

    setLastRenderTime(currentTime);
    animationFrameRef.current = null;
  }, [isVisible, enableTrails, enableGlow, performanceMode, lastRenderTime]);

  // Mouse interaction for adding particles
  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!interactive || !isRunning) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Add particle at mouse position with some randomness
    if (Math.random() < 0.1) { // 10% chance to spawn particle
      addParticleAt(x, y, {
        velocity: { min: 0.5, max: 1.2 },
        lifetime: { min: 30, max: 60 }
      });
    }
  }, [interactive, isRunning, addParticleAt]);

  // Handle click to add burst of particles
  const handleClick = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!interactive || !isRunning) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Add burst of particles
    for (let i = 0; i < 5; i++) {
      const offsetX = (Math.random() - 0.5) * 20;
      const offsetY = (Math.random() - 0.5) * 20;
      addParticleAt(x + offsetX, y + offsetY, {
        velocity: { min: 0.8, max: 2.0 },
        lifetime: { min: 40, max: 80 }
      });
    }
  }, [interactive, isRunning, addParticleAt]);

  // Initialize canvas on mount
  useEffect(() => {
    const cleanup = initializeCanvas();
    return cleanup;
  }, [initializeCanvas]);

  // Initialize particle system when worker is ready
  useEffect(() => {
    if (isWorkerReady && isVisible) {
      initializeParticleSystem({
        particleCount,
        colors,
        canvas: {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2)
        }
      });
    }
  }, [isWorkerReady, isVisible, particleCount, colors, initializeParticleSystem]);

  // Start/stop animation based on visibility
  useEffect(() => {
    if (!isWorkerReady) return;

    if (isVisible && !isRunning) {
      startAnimation();
    } else if (!isVisible && isRunning) {
      stopAnimation();
    }
  }, [isWorkerReady, isVisible, isRunning, startAnimation, stopAnimation]);

  // Update performance mode
  useEffect(() => {
    if (isWorkerReady && currentPerformanceMode !== performanceMode) {
      setPerformanceMode(performanceMode);
    }
  }, [isWorkerReady, performanceMode, currentPerformanceMode, setPerformanceMode]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Performance monitoring display (development only)
  const renderPerformanceOverlay = () => {
    if (process.env.NODE_ENV !== 'development' || !performanceMetrics) return null;

    return (
      <div 
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          background: 'rgba(0, 0, 0, 0.7)',
          color: '#00ff41',
          padding: '8px',
          fontSize: '12px',
          fontFamily: 'monospace',
          borderRadius: '4px',
          zIndex: 1000,
          pointerEvents: 'none'
        }}
      >
        <div>Performance Mode: {currentPerformanceMode}</div>
        <div>Avg Frame Time: {performanceMetrics.averageFrameTime.toFixed(2)}ms</div>
        <div>Particle Count: {performanceMetrics.particleCount}</div>
        <div>Dropped Frames: {performanceMetrics.droppedFrames}</div>
        <div>Worker Status: {isWorkerReady ? 'Ready' : 'Loading'}</div>
        <div>Animation: {isRunning ? 'Running' : 'Stopped'}</div>
      </div>
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      <canvas
        ref={canvasRef}
        className={`worker-particle-canvas ${className}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: interactive ? 'auto' : 'none',
          zIndex: -50,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.3s ease',
          contain: 'strict',
          ...style
        }}
        onMouseMove={interactive ? handleMouseMove : undefined}
        onClick={interactive ? handleClick : undefined}
      />
      {renderPerformanceOverlay()}
    </div>
  );
}; 